import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import api from "../../../api/api";
import Footer from "../../Footer/FooterBottom";
import ShowQuestionModal from "../../Home/Tracks/ExamView/ShowQuestionModal";
import SingleQuestionView from "../Question/View/QuestionView";
import CategoryTree from "./components/CategoryTree";
import QuestionsList from "./components/QuestionsList";
import { normalizeArabicText, stripHtml } from "./utils/arabicNormalizer";
import "./QuestionView.css";

const QuestionView = () => {
    const { t } = useTranslation("common");
    const history = useHistory();
    const user = useSelector(state => state.user);

    // Permission check: admin is strictly user type id 1
    const isAdmin = user && user.user && user.user.UserType && user.user.UserType.Id === 1;

    // Component State
    const [categoriesTree, setCategoriesTree] = useState([]);
    const [categoriesList, setCategoriesList] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loadingTree, setLoadingTree] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    // Collapsed states of category tree nodes (keyed by CategoryId, default to collapsed / null active)
    const [openCategoryId, setOpenCategoryId] = useState(null);

    // Search Filters
    const [topicSearchQuery, setTopicSearchQuery] = useState("");
    const [questionSearchQuery, setQuestionSearchQuery] = useState("");

    // Form States (Create Topic)
    const [newTopicName, setNewTopicName] = useState("");
    const [newTopicCategoryId, setNewTopicCategoryId] = useState("");
    const [showCreateForm, setShowCreateForm] = useState(false);

    // Form States (Rename Topic)
    const [editingTopicId, setEditingTopicId] = useState(null);
    const [editingTopicName, setEditingTopicName] = useState("");

    // Modal & Preview States
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [fetchingQuestionId, setFetchingQuestionId] = useState(null);

    // Change Topic States
    const [changingQuestion, setChangingQuestion] = useState(null);
    const [selectedNewTopicId, setSelectedNewTopicId] = useState("");
    const [savingTopicChange, setSavingTopicChange] = useState(false);
    const [modalOpenCategoryId, setModalOpenCategoryId] = useState(null);
    const [previewDetails, setPreviewDetails] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    useEffect(() => {
        if (isAdmin) {
            fetchCategoriesTree();
            fetchCategoriesList();
        } else if (user && user.isLogin === false) {
            history.push("/Login");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, user]);

    // Fetch questions when topic changes
    useEffect(() => {
        if (selectedTopic) {
            fetchQuestions(selectedTopic.TopicId);
        } else {
            setQuestions([]);
        }
        setQuestionSearchQuery(""); // Clear search filter
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTopic]);

    // API: Fetch categories tree (grouped with topics and question counts)
    const fetchCategoriesTree = async (autoSelectFirst = true) => {
        setLoadingTree(true);
        try {
            const response = await api.get("/QuestionsLookups/TopicsCategories");
            const data = response.data.data || [];
            setCategoriesTree(data);

            // Auto select first topic of the first category if none selected
            if (autoSelectFirst && data.length > 0 && !selectedTopic) {
                const firstCat = data.find(c => c.Topics && c.Topics.length > 0);
                if (firstCat && firstCat.Topics.length > 0) {
                    setSelectedTopic(firstCat.Topics[0]);
                }
            }
        } catch (error) {
            console.error("Error fetching categories tree:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء تحميل شجرة المواضيع");
        } finally {
            setLoadingTree(false);
        }
    };


    // API: Fetch simple categories list for dropdown selection
    const fetchCategoriesList = async () => {
        try {
            const response = await api.get("/QuestionsLookups/CategoriesList");
            setCategoriesList(response.data.data || []);
        } catch (error) {
            console.error("Error fetching categories list:", error);
        }
    };

    // API: Fetch questions for a topic
    const fetchQuestions = async (topicId) => {
        setLoadingQuestions(true);
        try {
            const response = await api.get(`/QuestionsLookups/Topics/${topicId}/Questions`);
            setQuestions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء تحميل الأسئلة");
        } finally {
            setLoadingQuestions(false);
        }
    };

    const handleViewQuestion = async (question) => {
        if (!question.QuestionId) return;
        setFetchingQuestionId(question.QuestionId);
        try {
            const response = await api.post("/QuestionsLookups", [
                {
                    IsFromDB: true,
                    QuestionId: question.QuestionId,
                    LessonId: question.LessonId
                }
            ]);
            const details = response.data.data?.[0];
            if (details) {
                setSelectedQuestion(details);
                setShowQuestionModal(true);
            } else {
                toast.error(t("QuestionView.errorLoadingDetails") || "تعذر تحميل تفاصيل السؤال");
            }
        } catch (error) {
            console.error("Error fetching question details:", error);
            toast.error(t("QuestionView.errorLoadingDetails") || "حدث خطأ أثناء تحميل تفاصيل السؤال");
        } finally {
            setFetchingQuestionId(null);
        }
    };

    // API: Create new topic at specific category
    const handleCreateTopic = async (e) => {
        e.preventDefault();
        if (!newTopicName.trim()) {
            toast.warn(t("QuestionView.titleRequired") || "يرجى إدخال اسم الموضوع");
            return;
        }

        const catIdValue = newTopicCategoryId ? parseInt(newTopicCategoryId) : 0;

        if (catIdValue < 1 || catIdValue > 5) {
            toast.warn(t("QuestionView.categoryRequired"));
            return;
        }

        try {
            const response = await api.post("/QuestionsLookups/Topics", {
                TopicName: newTopicName.trim(),
                CategoryId: catIdValue
            });
            const createdTopic = response.data.data;
            toast.success(t("ControlPanel.Indices.Success.Title") || "تم الحفظ بنجاح");

            // Reload the tree to update counts and hierarchy
            await fetchCategoriesTree(false);

            // Set newly created topic as selected
            setSelectedTopic({
                TopicId: createdTopic.TopicId,
                TopicName: createdTopic.TopicName,
                QuestionCount: 0
            });

            // Clear and close form
            setNewTopicName("");
            setNewTopicCategoryId("");
            setShowCreateForm(false);
        } catch (error) {
            console.error("Error creating topic:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء إضافة الموضوع");
        }
    };

    // API: Rename topic
    const handleRenameTopic = async (e, id) => {
        e.preventDefault();
        if (!editingTopicName.trim()) {
            toast.warn(t("QuestionView.titleRequired") || "يرجى إدخال اسم الموضوع");
            return;
        }

        try {
            const response = await api.put(`/QuestionsLookups/Topics/${id}`, {
                TopicName: editingTopicName.trim()
            });
            const updatedTopic = response.data.data;
            toast.success(t("ControlPanel.Indices.Success.Title") || "تم الحفظ بنجاح");

            // Update tree locally without fetching
            setCategoriesTree(prev =>
                prev.map(cat => ({
                    ...cat,
                    Topics: cat.Topics.map(t =>
                        t.TopicId === id ? { ...t, TopicName: updatedTopic.TopicName } : t
                    )
                }))
            );

            if (selectedTopic && selectedTopic.TopicId === id) {
                setSelectedTopic(prev => ({ ...prev, TopicName: updatedTopic.TopicName }));
            }

            setEditingTopicId(null);
            setEditingTopicName("");
        } catch (error) {
            console.error("Error renaming topic:", error);
            toast.error(t("ControlPanel.Indices.error.Title") || "حدث خطأ أثناء تعديل الاسم");
        }
    };

    const handleSaveTopicChange = async (e) => {
        e.preventDefault();
        if (!changingQuestion) return;
        if (!selectedNewTopicId) {
            toast.warn(t("QuestionView.selectTopic") || "يرجى اختيار موضوع");
            return;
        }

        setSavingTopicChange(true);
        try {
            await api.put(`/QuestionsLookups/Questions/${changingQuestion.QuestionId}/Topic`, {
                TopicId: parseInt(selectedNewTopicId)
            });
            toast.success(t("QuestionView.changeTopicSuccess") || "تم تغيير موضوع السؤال بنجاح");

            // Reload categories tree to update counts
            await fetchCategoriesTree(false);

            // Reload questions list for current selected topic
            if (selectedTopic) {
                await fetchQuestions(selectedTopic.TopicId);
            }

            setChangingQuestion(null);
            setSelectedNewTopicId("");
        } catch (error) {
            console.error("Error changing question topic:", error);
            toast.error(t("QuestionView.changeTopicError") || "حدث خطأ أثناء تغيير موضوع السؤال");
        } finally {
            setSavingTopicChange(false);
        }
    };

    const fetchPreviewDetails = async (question) => {
        if (!question.QuestionId) return;
        setLoadingPreview(true);
        try {
            const response = await api.post("/QuestionsLookups", [
                {
                    IsFromDB: true,
                    QuestionId: question.QuestionId,
                    LessonId: question.LessonId
                }
            ]);
            const details = response.data.data?.[0];
            if (details) {
                setPreviewDetails(details);
            } else {
                toast.error(t("QuestionView.errorLoadingDetails") || "تعذر تحميل تفاصيل السؤال");
            }
        } catch (error) {
            console.error("Error fetching preview details:", error);
            toast.error(t("QuestionView.errorLoadingDetails") || "حدث خطأ أثناء تحميل تفاصيل السؤال");
        } finally {
            setLoadingPreview(false);
        }
    };

    // Toggle expand/collapse for category nodes
    const toggleCategoryCollapse = (categoryId) => {
        setOpenCategoryId(prevId => prevId === categoryId ? null : categoryId);
    };

    // Filter categories tree based on search query
    const filteredCategoriesTree = useMemo(() => {
        if (!topicSearchQuery.trim()) return categoriesTree;

        const normalizedQuery = normalizeArabicText(topicSearchQuery.toLowerCase().trim());

        return categoriesTree.map(category => {
            // Filter topics under this category
            const matchingTopics = category.Topics.filter(topic => {
                const normalizedTopicName = normalizeArabicText(topic.TopicName.toLowerCase());
                return normalizedTopicName.includes(normalizedQuery);
            });

            const normalizedCategoryName = normalizeArabicText(category.CategoryName.toLowerCase());
            const isCategoryMatch = normalizedCategoryName.includes(normalizedQuery);

            if (isCategoryMatch || matchingTopics.length > 0) {
                return {
                    ...category,
                    Topics: matchingTopics // only show matching topics in search results
                };
            }
            return null;
        }).filter(Boolean);
    }, [categoriesTree, topicSearchQuery]);

    // Filter questions based on search query without html and without tashkeel
    const filteredQuestions = useMemo(() => {
        if (!questionSearchQuery.trim()) return questions;

        const normalizedQuery = normalizeArabicText(questionSearchQuery.toLowerCase().trim());

        return questions.filter(q => {
            const plainText = stripHtml(q.QuestionText);
            const normalizedText = normalizeArabicText(plainText.toLowerCase());
            return normalizedText.includes(normalizedQuery);
        });
    }, [questions, questionSearchQuery]);

    const startEditing = (topic) => {
        setEditingTopicId(topic.TopicId);
        setEditingTopicName(topic.TopicName);
    };

    const handleAccessDeniedBack = () => {
        history.push("/Profile");
    };

    if (!isAdmin) {
        return (
            <div className="access-denied-container">
                <Helmet>
                    <title>{t("QuestionView.accessDenied") || "Access Denied"} | {t("alarabeyya")}</title>
                </Helmet>
                <div className="access-denied-card text-center">
                    <div className="access-denied-icon">
                        <i className="fal fa-shield-ban"></i>
                    </div>
                    <h2 className="access-denied-title">{t("QuestionView.accessDenied")}</h2>
                    <p className="access-denied-text">{t("QuestionView.accessDeniedDesc")}</p>
                    <button className="btn btn-primary px-5 py-3 access-denied-btn" onClick={handleAccessDeniedBack}>
                        <i className="fal fa-arrow-right ml-2"></i>
                        <span>{t("QuestionView.cancel")}</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{t("QuestionView.title")} | {t("alarabeyya")}</title>
            </Helmet>

            <div className="question-view-admin-wrapper">
                {/* Header Banner */}
                <div className="admin-banner-header">
                    <div className="container-fluid px-lg-5 header-container-flex">
                        <div className="header-text-block">
                            <h2 className="banner-title">{t("QuestionView.title")}</h2>
                            <p className="banner-subtitle">{t("QuestionView.subtitle")}</p>
                        </div>
                        <div className="header-badge-block">
                            <div className="quick-stat-badge">
                                <span className="stat-value">
                                    {categoriesTree.reduce((acc, cat) => acc + (cat.Topics ? cat.Topics.length : 0), 0)}
                                </span>
                                <span className="stat-label">{t("QuestionView.topicCountLabel")}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container-fluid px-lg-5 py-5 main-dashboard-body">
                    <div className="row">
                        {/* Left Sidebar: Collapsible Categories Tree */}
                        <CategoryTree
                            topicSearchQuery={topicSearchQuery}
                            setTopicSearchQuery={setTopicSearchQuery}
                            showCreateForm={showCreateForm}
                            setShowCreateForm={setShowCreateForm}
                            handleCreateTopic={handleCreateTopic}
                            newTopicName={newTopicName}
                            setNewTopicName={setNewTopicName}
                            newTopicCategoryId={newTopicCategoryId}
                            setNewTopicCategoryId={setNewTopicCategoryId}
                            categoriesList={categoriesList}
                            loadingTree={loadingTree}
                            filteredCategoriesTree={filteredCategoriesTree}
                            openCategoryId={openCategoryId}
                            toggleCategoryCollapse={toggleCategoryCollapse}
                            selectedTopic={selectedTopic}
                            setSelectedTopic={setSelectedTopic}
                            editingTopicId={editingTopicId}
                            setEditingTopicId={setEditingTopicId}
                            editingTopicName={editingTopicName}
                            setEditingTopicName={setEditingTopicName}
                            handleRenameTopic={handleRenameTopic}
                            startEditing={startEditing}
                            t={t}
                        />

                        {/* Right Content: Questions List & Lesson Links */}
                        <QuestionsList
                            selectedTopic={selectedTopic}
                            filteredQuestions={filteredQuestions}
                            questions={questions}
                            questionSearchQuery={questionSearchQuery}
                            setQuestionSearchQuery={setQuestionSearchQuery}
                            loadingQuestions={loadingQuestions}
                            fetchingQuestionId={fetchingQuestionId}
                            handleViewQuestion={handleViewQuestion}
                            onChangeTopic={(q) => {
                                setChangingQuestion(q);
                                setPreviewDetails(null);
                                setSelectedNewTopicId(selectedTopic ? selectedTopic.TopicId : "");
                                if (selectedTopic) {
                                    const catNode = categoriesTree.find(cat =>
                                        cat.Topics && cat.Topics.some(t => t.TopicId === selectedTopic.TopicId)
                                    );
                                    if (catNode) {
                                        setModalOpenCategoryId(catNode.CategoryId);
                                    } else {
                                        setModalOpenCategoryId(null);
                                    }
                                } else {
                                    setModalOpenCategoryId(null);
                                }
                                fetchPreviewDetails(q);
                            }}
                            t={t}
                        />
                    </div>
                </div>
            </div>

            <div className="position-sticky mt-auto">
                <Footer />
            </div>

            {showQuestionModal && selectedQuestion && (
                <ShowQuestionModal
                    showResult={true}
                    handleClose={() => {
                        setShowQuestionModal(false);
                        setSelectedQuestion(null);
                    }}
                    renderQuestionView={() => (
                        <SingleQuestionView
                            question={selectedQuestion}
                            key={selectedQuestion.Id}
                            index={1}
                            showResult={false}
                            onEdit={true}
                            solveButton={false}
                        />
                    )}
                />
            )}

            {changingQuestion && (
                <Modal
                    centered
                    size="xl"
                    dialogClassName="custom-change-topic-modal"
                    show={true}
                    onHide={() => {
                        if (!savingTopicChange) {
                            setChangingQuestion(null);
                            setSelectedNewTopicId("");
                            setPreviewDetails(null);
                        }
                    }}
                    backdrop={savingTopicChange ? "static" : true}
                    keyboard={!savingTopicChange}
                >
                    <Modal.Header closeButton={!savingTopicChange}>
                        <Modal.Title>
                            <i className="fal fa-exchange-alt ml-2"></i>
                            {t("QuestionView.selectNewTopic") || "تغيير موضوع السؤال"}
                        </Modal.Title>
                    </Modal.Header>
                    <form onSubmit={handleSaveTopicChange}>
                        <Modal.Body style={{ minHeight: "450px" }}>
                            <div className="row">
                                {/* Right Side: Question Preview */}
                                <div className="col-lg-7 col-md-7 pl-lg-4">
                                    <h5 className="font-weight-bold mb-3 pb-2 border-bottom text-primary d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <span>
                                            <i className="fal fa-eye ml-2"></i>
                                            {t("QuestionView.previewQuestion") || "معاينة السؤال"}
                                        </span>
                                        {changingQuestion && changingQuestion.LessonName && (
                                            <span className="badge font-weight-normal py-2 px-3 text-right" style={{ fontSize: "0.8rem", borderRadius: "20px", backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", direction: "rtl" }}>
                                                <i className="fal fa-link ml-1"></i>
                                                {changingQuestion.LessonName}
                                            </span>
                                        )}
                                    </h5>

                                    {loadingPreview ? (
                                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                            <div className="loader-ring"><div></div><div></div><div></div><div></div></div>
                                            <p className="mt-3 text-muted">{t("QuestionView.pleaseWait")}</p>
                                        </div>
                                    ) : previewDetails ? (
                                        <div className="preview-container-wrapper position-relative p-3 border rounded bg-white shadow-sm" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                            <div className="overlayer-question"></div>
                                            <div style={{ pointerEvents: "none" }}>
                                                <SingleQuestionView
                                                    question={previewDetails}
                                                    key={previewDetails.Id}
                                                    index={1}
                                                    showResult={false}
                                                    onEdit={true}
                                                    solveButton={false}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            {t("QuestionView.errorLoadingDetails") || "حدث خطأ أثناء تحميل تفاصيل السؤال"}
                                        </div>
                                    )}
                                </div>

                                {/* Left Side: Topic Tree Selection */}
                                <div className="col-lg-5 col-md-5 border-left pr-lg-4">
                                    <h5 className="font-weight-bold mb-3 pb-2 border-bottom text-indigo" style={{ color: "#4f46e5" }}>
                                        <i className="fal fa-sitemap ml-2"></i>
                                        {t("QuestionView.chooseCategory") || "اختر موضوعاً"}
                                    </h5>

                                    <div
                                        className="change-topic-tree-container text-right"
                                        style={{
                                            maxHeight: "60vh",
                                            overflowY: "auto",
                                            border: "1px solid #cbd5e1",
                                            borderRadius: "12px",
                                            padding: "1rem",
                                            backgroundColor: "#f8fafc",
                                            direction: "rtl"
                                        }}
                                    >
                                        {categoriesTree.map((category) => {
                                            const isCollapsed = modalOpenCategoryId !== category.CategoryId;

                                            return (
                                                <div key={category.CategoryId} className="category-tree-node mb-2">
                                                    <div
                                                        className="category-node-header d-flex justify-content-between align-items-center p-2"
                                                        style={{ backgroundColor: "#f1f5f9", cursor: "pointer", borderBottom: isCollapsed ? "none" : "1px solid #e2e8f0" }}
                                                        onClick={() => setModalOpenCategoryId(prev => prev === category.CategoryId ? null : category.CategoryId)}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className={`fal ${isCollapsed ? 'fa-chevron-left' : 'fa-chevron-down'} collapse-arrow ml-2`} style={{ width: "12px" }}></i>
                                                            <i className="fal fa-folder-open category-folder-icon ml-2" style={{ color: "#f59e0b", fontSize: "1.1rem" }}></i>
                                                            <span className="category-node-title font-weight-bold" style={{ fontSize: "0.9rem" }}>
                                                                {category.CategoryName}
                                                                <span className="text-muted small mr-2">({category.Topics ? category.Topics.length : 0})</span>
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {!isCollapsed && (
                                                        <ul className="category-node-children list-unstyled m-0 p-1" style={{ backgroundColor: "#ffffff" }}>
                                                            {category.Topics && category.Topics.length > 0 ? (
                                                                category.Topics.map((topic) => {
                                                                    const isSelected = selectedNewTopicId === topic.TopicId || selectedNewTopicId.toString() === topic.TopicId.toString();

                                                                    return (
                                                                        <li
                                                                            key={topic.TopicId}
                                                                            className={`modal-topic-tree-item p-2 d-flex align-items-center justify-content-between ${isSelected ? 'active' : ''}`}
                                                                            style={{
                                                                                cursor: "pointer",
                                                                                transition: "all 0.2s",
                                                                                backgroundColor: isSelected ? "#4f46e5" : "transparent",
                                                                                color: isSelected ? "#ffffff" : "var(--text-slate-700)",
                                                                                borderRadius: "6px"
                                                                            }}
                                                                            onClick={() => setSelectedNewTopicId(topic.TopicId)}
                                                                        >
                                                                            <div className="d-flex align-items-center gap-2 w-100">
                                                                                <i className={`fal fa-file-alt ml-2 ${isSelected ? 'text-white' : 'text-muted'}`} style={{ fontSize: "0.85rem" }}></i>
                                                                                <span style={{ fontWeight: isSelected ? "700" : "500", fontSize: "0.85rem" }}>{topic.TopicName}</span>
                                                                            </div>
                                                                            {isSelected && <i className="fal fa-check-circle text-white"></i>}
                                                                        </li>
                                                                    );
                                                                })
                                                            ) : (
                                                                <li className="p-2 text-muted italic small">{t("QuestionView.noTopicsInCategory")}</li>
                                                            )}
                                                        </ul>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setChangingQuestion(null);
                                    setSelectedNewTopicId("");
                                    setPreviewDetails(null);
                                }}
                                disabled={savingTopicChange}
                            >
                                <i className="fal fa-times ml-1"></i>
                                {t("QuestionView.cancel")}
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={savingTopicChange || !selectedNewTopicId}
                            >
                                {savingTopicChange ? (
                                    <>
                                        <i className="fa-spin fas fa-spinner ml-1"></i>
                                        {t("QuestionView.pleaseWait")}
                                    </>
                                ) : (
                                    <>
                                        <i className="fal fa-save ml-1"></i>
                                        {t("QuestionView.save")}
                                    </>
                                )}
                            </button>
                        </Modal.Footer>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default QuestionView;
